/* eslint-disable camelcase */
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useAppSelector } from '@/Application/Hooks/Redux';
import type { TCardType } from '@/modules/perseo-billing/containers/payment-methods/components/spreedly-cards/hooks';
import type { IFormFieldSetters } from '@/modules/perseo-billing/containers/payment-methods/components/spreedly-cards/hooks/handle-field-event';
import handleFieldEvent from '@/modules/perseo-billing/containers/payment-methods/components/spreedly-cards/hooks/handle-field-event';
import handleReady from '@/modules/perseo-billing/containers/payment-methods/components/spreedly-cards/hooks/handle-ready';
import useHandlePaymentMethod from '@/modules/perseo-billing/containers/payment-methods/components/spreedly-cards/hooks/use-handle-payment-method';
import useSpreedlyPayment from '@/modules/perseo-billing/containers/payment-methods/components/spreedly-cards/hooks/use-spreedly-payment';
import { INPUT_STYLES } from '@/modules/perseo-billing/containers/payment-methods/components/spreedly-cards/styles';
import { useGtmBillingProduct } from '@/modules/perseo-billing/hooks';
import { PaymentProcessor } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { useMediaQuery } from '@/modules/perseo-billing/ui/react/src';
import { useCustomerContext } from '@/sections/customer/CustomerContext';

const SPREEDLY_ID = import.meta.env.VITE_SPREEDLY_CLIENT_ID ?? '';
const Spreedly = window.Spreedly;

const getTokenizedCreditCardData = (customerInfo: ICustomerData) => {
  const fullName = document.getElementById('full_name') as HTMLInputElement;
  const expirationDate = document.getElementById(
    'expiration_date'
  ) as HTMLInputElement;

  const firstName = fullName?.value.split(' ')[0];
  const lastName = fullName?.value.split(' ')[1];
  const month = expirationDate?.value.split('/')[0];
  const year = '20' + expirationDate?.value.split('/')[1];
  const userId = customerInfo?.customerEmail ?? '';
  const dni = customerInfo?.customerRut ?? '';

  return {
    email: userId,
    dni: dni,
    rut: dni,
    full_name: fullName?.value ?? '',
    first_name: firstName ?? '',
    last_name: lastName ?? '',
    month: month ?? '',
    year: year ?? ''
  };
};

const submitPaymentForm = (customerInfo?: ICustomerData) => {
  if (!Spreedly) return;
  if (!customerInfo) return;

  Spreedly.validate();
  const tokenizedCreditCardData = getTokenizedCreditCardData(customerInfo);
  Spreedly.tokenizeCreditCard(tokenizedCreditCardData);
};

export const useSpreedly = ({ methodId, isCencosud }: UseSpreedlyProps) => {
  const spreedlyHasInitializedRef = useRef(false);
  const acceptedBinsRef = useRef<string[]>([]);
  const billing = useAppSelector((state) => state.billing);

  const customerContext = useCustomerContext();
  const form = useFormContext<IFormFields>();

  const isTablet = useMediaQuery('lg');
  const handlePaymentMethod = useHandlePaymentMethod({
    methodId
  });
  const { sendSaveCardGtm } = useGtmBillingProduct();
  useSpreedlyPayment();

  const [errors, setErrors] = useState<spreedly.SpreedlyError[]>([]);
  const [isCardNumberHidden, setIsCardNumberHidden] = useState(true);
  const [cardType, setCardType] = useState<TCardType>('');
  const [cardNumberPreview, setCardNumberPreview] = useState('');
  const [isCardNumberValid, setIsCardNumberValid] = useState<boolean>(false);
  const [isCvvValid, setIsCvvValid] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState(false);

  const acceptedBinsList = billing?.availablePayments?.cencosudCardBINs ?? [];
  const selectorSubmitButton = isTablet
    ? '[data-testid="purchase-summary-continue-button"]'
    : '#submit-button';

  const spreedlyInstance = billing.availablePayments?.spreedlyInstance;

  const certificateToken = spreedlyInstance?.certificateToken ?? '';
  const nonce = spreedlyInstance?.nonce ?? '';
  const timestamp = spreedlyInstance?.timestamp ?? '';
  const signature = spreedlyInstance?.signature ?? '';

  const rut = customerContext.customer.data?.rut ?? '';
  const email = customerContext.customer.data?.email ?? '';

  const customerInfo = {
    customerRut: rut,
    customerEmail: email
  };

  const acceptedBins =
    methodId === PaymentProcessor.TRANSBANK_CENCOSUD_CARD
      ? acceptedBinsList
      : [];

  acceptedBinsRef.current = acceptedBins;

  const numberElementId = `spreedly-number-${methodId}`;
  const cvvElementId = `spreedly-cvv-${methodId}`;

  const getErrorsOfField = (field: string): string => {
    return errors.find((error) => error.attribute === field)?.message ?? '';
  };

  const submitPaymentFormWithContext = () => {
    const inputCheckSavedCard = document.querySelector(
      '[name=saveCard]'
    ) as HTMLInputElement;
    const isSaveCardChecked = inputCheckSavedCard?.checked;

    if (isSaveCardChecked) {
      sendSaveCardGtm(isCencosud);
    }
    submitPaymentForm(customerInfo);
  };

  const toggleMaskedFormat = () => {
    Spreedly.toggleMask();
    setIsCardNumberHidden(!isCardNumberHidden);
  };

  useEffect(() => {
    // * Check if window.Spreedly is available before initializing
    if (!Spreedly) return;

    Spreedly.removeHandlers();

    if (spreedlyHasInitializedRef.current) {
      Spreedly.reload();
    }

    Spreedly.init(SPREEDLY_ID, {
      numberEl: numberElementId,
      cvvEl: cvvElementId,
      nonce: nonce,
      timestamp: timestamp,
      certificateToken: certificateToken,
      signature: signature
    });

    spreedlyHasInitializedRef.current = true;

    // * On ready set the form field types and styles
    Spreedly.on('ready', handleReady);

    // * On field event handler for styles and validation
    Spreedly.on('fieldEvent', function (name, event, activeElement, inputData) {
      const formSetters: IFormFieldSetters = {
        setCardNumberPreview,
        setIsFocused,
        setIsCardNumberValid,
        setIsCvvValid,
        errors,
        setErrors,
        acceptedBins: acceptedBinsRef.current
      };
      handleFieldEvent(name, event, activeElement, formSetters, inputData);
    });

    //* when the payment method is tokenized and available to initiate the payment
    Spreedly.on('paymentMethod', function (token, pmData) {
      if (!handlePaymentMethod) return;

      const installments = form.watch('installments');

      handlePaymentMethod.handlePayment(token, pmData, +installments);
    });

    Spreedly.on('validation', function (inputProperties) {
      if (inputProperties?.cardType === 'visa') {
        setCardType('visa');
      }
      if (inputProperties?.cardType === 'master') {
        setCardType('mastercard');
      }
      if (inputProperties?.cardType === 'american_express') {
        setCardType('american-express');
      }
    });

    Spreedly.on('errors', function (errors: spreedly.SpreedlyError[]) {
      const errorMessages = errors.map((error) => {
        const field = error.attribute as spreedly.SpreedlyField;
        Spreedly.setStyle(field, INPUT_STYLES.errorNumber);
        return error;
      });

      setErrors(errorMessages);
    });

    const formEl = document.querySelector(selectorSubmitButton);
    formEl?.addEventListener('click', submitPaymentFormWithContext);

    return () => {
      const formElCleanup = document.querySelector(selectorSubmitButton);
      formElCleanup?.removeEventListener('click', submitPaymentFormWithContext);
      Spreedly.removeHandlers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid re-subscribing on every render
  }, [
    certificateToken,
    methodId,
    nonce,
    selectorSubmitButton,
    signature,
    timestamp
  ]);

  return {
    errors,
    getErrorsOfField,
    isCardNumberHidden,
    toggleMaskedFormat,
    isCardNumberValid,
    isCvvValid,
    cardType,
    cardNumberPreview,
    isFocused,
    spreedlyNumberElementId: numberElementId,
    spreedlyCvvElementId: cvvElementId
  };
};
